/**
 * Public response + admin write schemas for the "Scripts & tags" settings tab.
 * Backed by AppConfigEntry rows whose key starts with `scripts.`. The write
 * schema's key regex enforces that prefix so a scripts PATCH cannot stomp
 * keys in a sibling namespace (e.g. `branding.appName`).
 */

import { z } from 'zod';

/** Public response shape served by `GET /website/scripts`. */
export const WebsiteScriptsConfigSchema = z.object({
  gtm_id: z
    .string()
    .regex(/^GTM-[A-Z0-9]+$/u, 'Expected a GTM container id like "GTM-XXXXXX"')
    .nullable(),
  ga4_id: z
    .string()
    .regex(/^G-[A-Z0-9]+$/u, 'Expected a GA4 measurement id like "G-XXXXXXXXXX"')
    .nullable(),
  meta_pixel_id: z
    .string()
    .regex(/^\d{8,20}$/u, 'Expected an 8-20 digit numeric pixel id')
    .nullable(),
  /**
   * Separate Meta Pixel for CRM / Instagram-form (instaform) lead conversions.
   * Reference-only today: the website renders `meta_pixel_id`, and server-side
   * Meta Conversions API uses its own `dataset_id` secret - neither consumes
   * this field. Stored here so the operator has one place to record it.
   */
  instaform_pixel_id: z
    .string()
    .regex(/^\d{8,20}$/u, 'Expected an 8-20 digit numeric pixel id')
    .nullable(),
  clarity_id: z
    .string()
    .regex(/^[a-z0-9]+$/u, 'Expected a lowercase alphanumeric project id')
    .nullable(),
  customerlabs_id: z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/u, 'Expected a CustomerLabs account id (alphanumeric)')
    .nullable(),
  /**
   * Master on/off for the server-side Meta Conversions API (CAPI). When false,
   * the CRM sends NO CAPI events to Meta regardless of the Secrets Manager
   * config - a UI kill switch. Default true (defer to the secret-based config).
   * Stored as `scripts.meta_capi_enabled` = "true" | "false".
   */
  meta_capi_enabled: z.boolean(),
  /**
   * Pause ONLY the `Lead` CAPI cohort (lead-intake events) while every other
   * cohort (Converted / Qualified / PartialQualified / NonQualified) keeps
   * firing. Independent of `meta_capi_enabled` (the master switch). Default true
   * (Lead events sent). Stored as `scripts.meta_capi_lead_enabled` = "true" | "false".
   */
  meta_capi_lead_enabled: z.boolean(),
});
export type WebsiteScriptsConfig = z.infer<typeof WebsiteScriptsConfigSchema>;

/** Admin bulk-upsert write schema (`PATCH /admin/website/scripts`). */
export const WebsiteScriptsEntrySchema = z.object({
  key: z
    .string()
    .regex(/^scripts\.[a-z0-9_]+$/u, 'Key must start with "scripts."')
    .max(120),
  value: z.union([z.string(), z.null()]),
});
export type WebsiteScriptsEntry = z.infer<typeof WebsiteScriptsEntrySchema>;

export const AdminWebsiteScriptsRequestSchema = z.object({
  entries: z.array(WebsiteScriptsEntrySchema).min(1).max(8),
});
export type AdminWebsiteScriptsRequest = z.infer<typeof AdminWebsiteScriptsRequestSchema>;
